import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Loader2, Lock, ShieldAlert } from "lucide-react";
import { login, setToken, clearToken } from "../api";
import { useStudio } from "./StudioContext";
import { T, INP } from "./theme";

function formatErr(detail) {
  if (!detail) return "Login failed";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

function YosiMark() {
  return (
    <div className="flex items-center gap-2.5">
      <span
        className="inline-flex items-center justify-center w-11 h-11 rounded-xl font-black text-xl"
        style={{ background: `linear-gradient(135deg, ${T.gold}, ${T.goldHi})`, color: "#0A0A0C" }}
      >
        Y
      </span>
      <div className="leading-tight">
        <div className="font-display font-extrabold text-lg tracking-tight" style={{ color: T.text }}>
          Yosi Media Studio
        </div>
        <div className="text-[10px] tracking-[0.25em] uppercase" style={{ color: T.gold }}>
          Agency Portal
        </div>
      </div>
    </div>
  );
}

export default function StudioLogin() {
  const { setUser } = useStudio();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const data = await login(email, password);
      if (data.user?.role !== "super_admin") {
        clearToken();
        setErr("This portal is restricted to Yosi Media Studio super administrators.");
        return;
      }
      setToken(data.token);
      setUser(data.user);
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 relative"
      style={{
        background: `radial-gradient(1200px 600px at 20% -10%, rgba(212,175,55,0.12), transparent 60%), radial-gradient(900px 500px at 90% 110%, rgba(212,175,55,0.07), transparent 55%), ${T.bg}`,
      }}
    >
      <Helmet>
        <title>Yosi Media Studio | Agency Portal</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <form
        onSubmit={submit}
        data-testid="studio-login-form"
        className="relative z-10 w-full max-w-md p-8 rounded-2xl"
        style={{ background: T.card, border: `1px solid ${T.border}`, boxShadow: T.shadow }}
      >
        <div className="mb-7 flex items-center justify-between">
          <YosiMark />
          <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full"
                style={{ background: T.goldSoft, color: T.gold }}>
            <ShieldAlert size={10} /> Super Admin
          </span>
        </div>

        <h1 className="font-display font-extrabold text-2xl mb-1" style={{ color: T.text }}>
          Agency master dashboard
        </h1>
        <p className="text-sm mb-6" style={{ color: T.textDim }}>
          Sign in to manage every Yosi Media Studio client website.
        </p>

        <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: T.textDim }}>Email</label>
        <input data-testid="studio-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          className="w-full mb-4 px-4 py-3 text-sm outline-none rounded-lg" style={INP} />

        <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: T.textDim }}>Password</label>
        <input data-testid="studio-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
          className="w-full mb-5 px-4 py-3 text-sm outline-none rounded-lg" style={INP} />

        {err && (
          <p data-testid="studio-login-error" className="mb-4 text-sm px-3 py-2 rounded-lg"
            style={{ background: "rgba(239,68,68,0.12)", color: "#fca5a5" }}>{err}</p>
        )}

        <button type="submit" disabled={loading} data-testid="studio-login-submit"
          className="w-full rounded-lg py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-transform active:scale-[0.98]"
          style={{ background: `linear-gradient(135deg, ${T.gold}, ${T.goldHi})`, color: "#0A0A0C" }}>
          {loading && <Loader2 size={18} className="animate-spin" />} <Lock size={14} /> Enter Studio
        </button>

        <p className="mt-6 text-[11px] text-center" style={{ color: T.textFaint }}>
          Yosi Media Studio · Agency access · Restricted platform
        </p>
      </form>
    </div>
  );
}
